import { Row, Tooltip, Text, StatProps, Column } from "@bridgesplit/ui";
import { Divider } from "@mui/material";
import {
    BsMetaUtil,
    convertExternalRewardToApy,
    useVaultApy,
    getVaultLpVirtualPrice,
    LendingVaultExpanded,
    ParsedVaultRewardsScheduleExpanded,
    useOraclePrices,
    isRewardsActive,
    getLendingVaultMints
} from "@bridgesplit/abf-react";
import { formatPercent, getUnixTs } from "@bridgesplit/utils";
import { TokenImage } from "app/components/common";

import { BaseApy } from "./BaseApy";
import { CaptionWithIcon, ExternalRewardsDisplay, RateDisplay, TooltipContent } from "./common";
import { RewardsDailyPoints } from "./daily-rewards";
import { RewardsExternalPointsRewards } from "./external-point-rewards/RewardsExternalPointsReward";
import { RewardsLendVaultApyProps } from "./types";

export function RewardsLendVaultApy(props: RewardsLendVaultApyProps) {
    const blendedApy = useVaultApy(props);

    return (
        <Tooltip noPadding title={<RewardsLendVaultTooltip {...props} />} reverseColors>
            <Row spacing={1}>
                <ExternalRewardsDisplay
                    {...props}
                    externalRewardsVault={props.lendingVaultExpanded?.vaultRewardsSchedules}
                />
                <RateDisplay apy={blendedApy} variant="body1" color="body" isFeatured={props.isFeatured} />
            </Row>
        </Tooltip>
    );
}

function RewardsLendVaultTooltip(props: RewardsLendVaultApyProps) {
    const externalRewardStats = useExternalRewardStats(props);

    return (
        <TooltipContent px={0} py={1.5} spacing={1}>
            <Column px={1.5} spacing={0.5}>
                <BaseApy variant="body2" {...props} additionalStats={externalRewardStats} />
            </Column>
            <Divider />
            <RewardsDailyPoints {...props} />
            {props.externalPointRewards && (
                <>
                    <Divider />

                    <RewardsExternalPointsRewards {...props} />
                </>
            )}
        </TooltipContent>
    );
}

export function useExternalRewardStats({
    lendingVaultExpanded,
    depositAmount = 0
}: {
    lendingVaultExpanded: LendingVaultExpanded | undefined;
    depositAmount?: number;
}): StatProps[] {
    const rewardMintsWithPrincipal = getLendingVaultMints(lendingVaultExpanded);
    const { getUsdPrice } = useOraclePrices(rewardMintsWithPrincipal);
    if (!lendingVaultExpanded) return [];
    const now = getUnixTs();
    const lpPrice = getVaultLpVirtualPrice(lendingVaultExpanded, now);
    const principalPrice = getUsdPrice(lendingVaultExpanded?.vault.principalMint);
    const lpSupply = lendingVaultExpanded.vault.lpSupply;
    const activeRewards = lendingVaultExpanded.vaultRewardsSchedules.filter((schedule) => isRewardsActive(schedule));
    return activeRewards.map((schedule) =>
        createExternalRewardStat(
            schedule,
            lpPrice,
            principalPrice,
            lpSupply,
            getUsdPrice(schedule.rewardMetadata.mint) ?? 0,
            depositAmount
        )
    );
}

function createExternalRewardStat(
    schedule: ParsedVaultRewardsScheduleExpanded,
    lpPrice: number | undefined,
    principalPrice: number | undefined,
    lpSupply: number,
    rewardTokenPrice: number,
    depositAmount = 0
): StatProps {
    const apy = convertExternalRewardToApy(
        schedule,
        lpPrice,
        principalPrice,
        lpSupply,
        rewardTokenPrice,
        depositAmount
    );
    const symbol = BsMetaUtil.getSymbol(schedule.rewardMetadata);

    return {
        value: (
            <Text color="success" variant="body2">
                +{formatPercent(apy)}
            </Text>
        ),
        caption: (
            <CaptionWithIcon
                variant="body2"
                caption={symbol}
                icon={<TokenImage metadata={schedule.rewardMetadata} size="sm" />}
            />
        )
    };
}
